import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';

import PropTypes from 'prop-types';

import { Box, Button, Link } from '@mui/material';

import { CircularLoaderComponent } from '../LoadingSpinner/LoadingSpinnerComponent';
import AssetViewComponent from './AssetViewComponent';

import { notifyUser } from '../Notification/actions';
import { getAssetUrl, validateAssetUrl } from './actions';

import {
  fileCodeAssetNotFound,
  fileCodeError,
  fileKeyDecryptionError,
} from '../Shared/constants/errorCodes';

import * as commonSelectors from '../Shared/selectors';
import * as selectors from './selectors';

import { ButtonLink } from '../Button';
import StyledTextField from '../Shared/StyledTextField';

const AssetViewContainer = (props) => {
  const {
    assetKey,
    assetCode,
    getAssetUrl,
    isLoading,
    assetUrl,
    contactInfo,
    firstName,
    contactType,
    errorCode,
  } = props;

  const [verificationCode, setVerificationCode] = useState(null);
  const [urlValidation, setUrlValidation] = useState(null);

  useEffect(() => {
    if (assetKey && assetCode) {
      getAssetUrl({ key: assetKey, code: assetCode });
    } else {
      assetKey && getAssetUrl({ key: assetKey, code: null });
    }
  }, []);

  const handleAssetUrl = async () => {
    setUrlValidation('in_progress');
    const isValidUrl = await validateAssetUrl(assetUrl);
    if (isValidUrl) {
      setUrlValidation('valid');
      window.location.assign(assetUrl);
    } else {
      setUrlValidation('invalid');
    }
  };

  if (assetUrl && !urlValidation) handleAssetUrl();

  const handleChange = (event) => {
    setVerificationCode(event.target.value);
  };

  const onCodeSubmit = () => {
    getAssetUrl({ key: assetKey, code: verificationCode });
  };

  const getAssetContent = () => {
    const carsonSupport = (
      <Link component={RouterLink} to="/support" color="primary">
        Carson Support
      </Link>
    );

    const verificationCodeInput = (
      <Box display="flex" flexDirection="row">
        <StyledTextField
          id="verification-code"
          label="Code"
          type="text"
          value={verificationCode}
          onChange={handleChange}
        />
        <Box ml="2rem" />
        <Button
          type="button"
          id="verification-code-submit"
          variant="contained"
          color="primary"
          disableElevation
          disabled={!verificationCode}
          fullWidth={false}
          onClick={onCodeSubmit}
        >
          Submit
        </Button>
      </Box>
    );

    let title;
    let subtitle;
    let content;

    if (urlValidation === 'invalid') {
      title = `Do you need a new link?`;
      subtitle = (
        <span>
          The link you used has expired. Contact {carsonSupport} to get a new
          one
        </span>
      );
    } else if (urlValidation === 'in_progress') {
      title = `Your document will be opened in a few seconds`;
      content = <CircularLoaderComponent />;
    } else if (isLoading) {
      content = <CircularLoaderComponent />;
    } else if (!assetKey && !errorCode) {
      title = `Did you get here by mistake?`;
      subtitle = `Your request is missing some key information. Please click the original link to view your document`;
    } else if (assetKey && errorCode === fileKeyDecryptionError) {
      title = `Did your information change?`;
      subtitle = `Your request is missing key information. Please use the original to view your document`;
    } else if (assetKey && errorCode === fileCodeError) {
      title = `Do you need a new code?`;
      subtitle = `Your have entered a wrong or an expired code. Click below to request a new code`;
      content = (
        <Box display="flex" flexDirection="column">
          {verificationCodeInput}
          <Box mt="1rem" />
          <ButtonLink
            onClick={() => getAssetUrl({ key: assetKey, code: null })}
          >
            Send me a new code
          </ButtonLink>
        </Box>
      );
    } else if (assetKey && errorCode === fileCodeAssetNotFound) {
      title = `Do you need a new link?`;
      subtitle = (
        <span>
          {firstName}, the code you entered is correct, but we can’t find the
          document you requested. Contact {carsonSupport} to get a new link
        </span>
      );
    } else if (assetKey && contactInfo) {
      title = `Confirmation code is required`;
      subtitle = `We’ve sent a confirmation code to your ${contactInfo} ${
        contactType === 'email' ? 'email address' : 'phone number'
      }. Please check your ${
        contactType === 'email' ? 'email' : 'messages'
      } and enter the code below. Note that the code will expire in 15 minutes.`;
      content = verificationCodeInput;
    } else {
      content = <CircularLoaderComponent />;
    }
    return { title, subtitle, content };
  };

  return <AssetViewComponent getAssetContent={getAssetContent} />;
};

AssetViewContainer.defaultProps = {
  assetKey: null,
  assetCode: null,
  assetUrl: null,
  contactInfo: null,
  firstName: null,
  contactType: null,
  errorCode: null,
};

AssetViewContainer.propTypes = {
  getAssetUrl: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  assetKey: PropTypes.string,
  assetCode: PropTypes.string,
  assetUrl: PropTypes.string,
  contactInfo: PropTypes.string,
  firstName: PropTypes.string,
  contactType: PropTypes.string,
  errorCode: PropTypes.number,
};

const mapStateToProps = (state, ownProps) => ({
  assetKey: selectors.getAssetKey(ownProps),
  assetCode: selectors.getAssetCode(ownProps),
  assetUrl: selectors.getAssesUrl(state),
  contactInfo: selectors.getContactInfo(state),
  firstName: selectors.getContactFirstName(state),
  contactType: selectors.getContactType(state),
  errorCode: selectors.getAssetErrorCode(state),
  isLoading: commonSelectors.getIsLoading(state),
});

const mapDispatchToProps = (dispatch) => ({
  getAssetUrl: (data) => getAssetUrl(dispatch, data),
  notifyUser: (message, isSuccess) => notifyUser(dispatch, message, isSuccess),
});

export default connect(mapStateToProps, mapDispatchToProps)(AssetViewContainer);
